// Note use of Braid's built-in `characterLimit` for input fields.
// These count in UTF-16 code units which is _fine_ but suboptimal.
// https://developer.seek.com/graphql/seek-api-conventions#string-lengths

import {
  Alert,
  Divider,
  RadioGroup,
  RadioItem,
  Secondary,
  Stack,
  Strong,
  Text,
  Textarea,
  TextField,
} from 'braid-design-system';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker } from '#src/components/DatePicker/DatePicker.tsx';
import { ThemedExample } from '#src/components/ThemedExample/ThemedExample.tsx';

const WorkArrangementLabels: Record<string, string> = {
  OnSite: 'On site',
  Remote: 'Remote',
  Hybrid: 'Hybrid',
};

export const WorkArrangements = () => {
  const { control } = useForm<{ seekWorkArrangement: string }>();

  return (
    <ThemedExample>
      <Controller
        render={({ field: { ref, value, ...field } }) => (
          <RadioGroup
            {...field}
            id="seekWorkArrangement"
            label="Work arrangement"
            value={value ?? ''}
          >
            <RadioItem label={WorkArrangementLabels.OnSite} value="OnSite" />
            <RadioItem label={WorkArrangementLabels.Hybrid} value="Hybrid" />
            <RadioItem label={WorkArrangementLabels.Remote} value="Remote" />
          </RadioGroup>
        )}
        name="seekWorkArrangement"
        control={control}
      />
    </ThemedExample>
  );
};

export const WorkTypes = () => {
  const { control } = useForm<{ seekAnzWorkTypeCode: string }>();

  return (
    <ThemedExample>
      <Controller
        render={({ field: { ref, value, ...field } }) => (
          <RadioGroup
            {...field}
            id="seekAnzWorkTypeCode"
            label="Work type"
            value={value ?? ''}
          >
            <RadioItem label="Full time" value="FullTime" />
            <RadioItem label="Part time" value="PartTime" />
            <RadioItem label="Contract" value="ContractTemp" />
            <RadioItem label="Casual" value="Casual" />
          </RadioGroup>
        )}
        name="seekAnzWorkTypeCode"
        control={control}
      />
    </ThemedExample>
  );
};

export const JobTitle = () => {
  const { control } = useForm<{ positionTitle: string }>();

  return (
    <ThemedExample>
      <Controller
        render={({ field: { value, ...field } }) => (
          <TextField
            {...field}
            // https://developer.seek.com/schema/named-type/PostPosition_PositionProfileInput#/field/positionTitle
            characterLimit={80}
            id="positionTitle"
            label="Job title"
            value={value ?? ''}
          />
        )}
        defaultValue="Associate Developer"
        name="positionTitle"
        control={control}
        rules={{ required: 'Enter a job title' }}
      />
    </ThemedExample>
  );
};

export const SeekVideo = () => {
  const { control } = useForm<{
    seekVideo: string;
    seekAnzPositionCode: string;
  }>();

  return (
    <ThemedExample>
      <Stack space="large">
        <Controller
          render={({ field: { value, ...field } }) => (
            <TextField
              {...field}
              // https://developer.seek.com/schema/named-type/SeekVideoInput#/field/url
              characterLimit={255}
              id="seekVideo"
              label="Video URL"
              value={value ?? ''}
              placeholder="https://www.youtube.com/watch?v=Sx40s6kn64s"
            />
          )}
          defaultValue="https://www.youtube.com/watch?v=Sx40s6kn64s"
          name="seekVideo"
          control={control}
          rules={{ required: 'Enter a video URL' }}
        />
        <Controller
          name="seekAnzPositionCode"
          control={control}
          render={({ field }) => (
            <RadioGroup
              {...field}
              id="seekAnzPositionCode"
              label="Video position"
              value={field.value ?? 'above'}
            >
              <RadioItem label="Above" value="above" />
              <RadioItem label="Below" value="below" />
            </RadioGroup>
          )}
        />
      </Stack>
    </ThemedExample>
  );
};

export const EndDate = () => {
  const { control } = useForm<{ end: string }>();
  return (
    <ThemedExample>
      <Controller
        name="end"
        control={control}
        rules={{
          validate: (value: string) => {
            if (new Date(value) < new Date()) {
              return 'Provide a date and time in the future';
            }
          },
        }}
        render={({ field }) => (
          <DatePicker
            name="end"
            type="date"
            onChange={(_, date) => field.onChange(date?.toISOString() ?? '')}
            value={field.value ? new Date(field.value) : new Date()}
            label="End date"
          />
        )}
      />
    </ThemedExample>
  );
};

export const PositionFormattedDescriptions = () => {
  const { control } = useForm<{
    searchSummary: string;
    searchBulletPoints: string[];
    advertisementDetails: string;
  }>();

  const limit = 3;
  const [bullets, setBullets] = useState<string[]>(Array(limit).fill(''));

  if (bullets.length !== limit) {
    const diff = limit - bullets.length;
    setBullets([...bullets, ...Array(diff).fill('')]);
  }
  return (
    <ThemedExample>
      <Stack space="large">
        <Controller
          render={({ field: { value, ...field } }) => (
            <Textarea
              {...field}
              // https://developer.seek.com/schema/named-type/PositionFormattedDescriptionInput#/field/content
              characterLimit={15000}
              id="advertisementDetails"
              label="Job description"
              value={value ?? ''}
            />
          )}
          control={control}
          name="advertisementDetails"
          rules={{ required: 'Enter a job description' }}
          defaultValue="A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area."
        />

        <Alert tone="info">
          <Text>
            Provide a rich text editor for the job description that outputs HTML
            markup.
          </Text>
        </Alert>

        <Divider />

        <Controller
          render={({ field: { value, ...field } }) => (
            <TextField
              {...field}
              // https://developer.seek.com/schema/named-type/PositionFormattedDescriptionInput#/field/content
              characterLimit={150}
              description="Write a sentence about your role to entice candidates to look at your job ad."
              id="searchSummary"
              label="Search summary"
              value={value ?? ''}
            />
          )}
          control={control}
          defaultValue="Start your software development career with a dynamic & growing team"
          name="searchSummary"
          rules={{ required: 'Enter a search summary' }}
        />

        <Divider />

        <Controller
          render={({ field: { onChange } }) => (
            <>
              <Stack space="medium">
                <Text>
                  <Strong>Key selling points</Strong>{' '}
                  <Secondary>(optional)</Secondary>
                </Text>
                <Text tone="secondary">
                  Enter up to {limit} key selling points to attract candidates
                  to view your role.
                </Text>
                <Stack space="small">
                  {bullets.map((bullet, index) => (
                    <TextField
                      aria-label={`Key selling point ${index}`}
                      // https://developer.seek.com/schema/named-type/PositionFormattedDescriptionInput#/field/content
                      characterLimit={80}
                      id={`searchBullet${index}`}
                      key={`bullet-${index}`}
                      onChange={(e) => {
                        const newState = [
                          ...bullets.slice(0, index),
                          e.currentTarget.value,
                          ...bullets.slice(index + 1),
                        ];
                        setBullets(newState);
                        onChange(newState);
                      }}
                      value={bullet}
                    />
                  ))}
                </Stack>
              </Stack>
            </>
          )}
          control={control}
          name="searchBulletPoints"
        />
      </Stack>
    </ThemedExample>
  );
};
